import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { SHOPIFY_STOREFRONT_URL, SHOPIFY_COLLECTION_URL } from "@/lib/api";

const LINKS = [
  { name: "HOME", path: "/" },
  { name: "MUSIC", path: "/music" },
  { name: "VIDEOS", path: "/videos" },
  { name: "PASSPORT", path: "/passport" },
  { name: "SHOP", path: "/shop", badge: "DROP 01" },
  { name: "BIO", path: "/bio" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { pathname } = useLocation();

  return (
    <>
      <header
        data-testid="main-header-nav"
        className="sticky top-0 z-50 bg-[#08080A]/90 backdrop-blur-md border-b border-[#1A1A22]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0" aria-label="CHILLWOAH home">
            <LogoMark size={38} />
            <span className="font-display font-extrabold text-xl tracking-tight text-white hidden sm:block">
              CHILLWOAH
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {LINKS.map((l) =>
              l.path === "/shop" ? (
                <a
                  key={l.path}
                  href={SHOPIFY_COLLECTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-link-shop"
                  className="relative px-4 py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors text-zinc-200 hover:text-white"
                >
                  SHOP
                  <span className="ml-1 align-top font-mono text-[10px] font-semibold text-[#F26522]">DROP 01</span>
                </a>
              ) : (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`nav-link-${l.name.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative px-4 py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors ${
                      isActive ? "text-[#F26522]" : "text-zinc-200 hover:text-white"
                    }`
                  }
                >
                  {l.name}
                  {pathname === l.path && (
                    <span className="absolute left-3 right-3 bottom-1 h-0.5 bg-[#F26522]" aria-hidden="true" />
                  )}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="cart-button"
              onClick={() => setCartOpen(true)}
              aria-label={`Open cart, ${count} item${count === 1 ? "" : "s"}`}
              className="relative min-w-[52px] h-[52px] px-4 flex items-center justify-center gap-2 border border-[#2A2A36] bg-[#121218] hover:border-[#F26522] transition-colors"
            >
              <ShoppingBag className="w-5 h-5 text-white" aria-hidden="true" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 flex items-center justify-center rounded-full bg-[#F26522] text-black font-mono text-xs font-bold"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="md:hidden min-w-[52px] h-[52px] px-4 flex items-center justify-center border border-[#2A2A36] bg-[#121218] hover:border-[#F26522] transition-colors"
            >
              <Menu className="w-6 h-6 text-white" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[70] bg-[#08080A] flex flex-col" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="h-16 sm:h-20 px-4 sm:px-6 flex items-center justify-between border-b border-[#1A1A22]">
            <div className="flex items-center gap-3">
              <LogoMark size={34} />
              <span className="font-display font-extrabold text-lg text-white">CHILLWOAH</span>
            </div>
            <button
              data-testid="mobile-menu-close"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="min-w-[52px] h-[52px] px-4 flex items-center justify-center border border-[#2A2A36] bg-[#121218]"
            >
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-10 flex flex-col gap-1" aria-label="Mobile navigation">
            {LINKS.map((l) =>
              l.path === "/shop" ? (
                <a
                  key={l.path}
                  href={SHOPIFY_COLLECTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-link-shop"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-5 border-b border-[#1A1A22] font-display font-extrabold uppercase text-3xl text-white active:text-[#F26522]"
                >
                  <span>SHOP</span>
                  <span className="font-mono text-xs text-[#F26522] tracking-[0.2em]">DROP 01</span>
                </a>
              ) : (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`mobile-nav-link-${l.name.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-5 border-b border-[#1A1A22] font-display font-extrabold uppercase text-3xl text-white active:text-[#F26522]"
                >
                  <span>{l.name}</span>
                  {l.badge && <span className="font-mono text-xs text-[#F26522] tracking-[0.2em]">{l.badge}</span>}
                </Link>
              )
            )}
            <p className="mt-10 font-mono text-sm tracking-[0.25em] text-[#F26522] uppercase">
              Music • Culture • Community
            </p>
            <p className="mt-2 font-editorial italic text-xl text-[#F5E6D3]">Independent Sound. Distinct Identity.</p>
          </nav>
        </div>
      )}
    </>
  );
}
